<template>
    <v-container fluid>
        <div class="update-doc-gnc">
            <h2 class="mb-4">Documentation GNC</h2>

            <!-- Mise à jour des documents actuels -->
            <v-card class="pa-4">
                <v-card-title>
                    <h3>Mettre à jour la documentation</h3>
                </v-card-title>

                <v-card-text>
                    <!-- Zone de drag and drop -->
                    <div
                        ref="dropZone"
                        class="drop-zone"
                        :class="{
                        'drop-zone--active': isDragOver,
                        'drop-zone--has-file': selectedFile
                    }"
                        @drop="onDrop"
                        @dragover="onDragOver"
                        @dragenter="onDragEnter"
                        @dragleave="onDragLeave"
                        @click="$refs.fileInput.click()"
                    >
                        <div class="drop-zone__content">
                            <v-icon
                                :size="selectedFile ? 40 : 60"
                                :color="isDragOver ? 'primary' : (selectedFile ? 'success' : 'grey')"
                                class="mb-3"
                            >
                                {{ selectedFile ? 'check-circle' : (isDragOver ? 'cloud-upload' : 'file-pdf-box') }}
                            </v-icon>

                            <div v-if="!selectedFile">
                                <h4 class="mb-2" :class="isDragOver ? 'primary--text' : ''">
                                    {{ isDragOver ? 'Relâchez pour uploader' : 'Glissez-déposez votre fichier PDF ici' }}
                                </h4>
                                <p class="grey--text">ou cliquez pour sélectionner un fichier</p>
                                <p class="caption grey--text">Maximum 10MB</p>
                            </div>

                            <div v-else>
                                <h4 class="success--text mb-2">{{ selectedFile.name }}</h4>
                                <v-chip
                                    color="success"
                                    text-color="white"
                                    small
                                >
                                    <v-icon left small>check</v-icon>
                                    {{ formatFileSize(selectedFile.size) }}
                                </v-chip>
                                <p class="caption mt-2">Cliquez pour changer de fichier</p>
                            </div>
                        </div>
                    </div>

                    <!-- Input file caché -->
                    <input
                        ref="fileInput"
                        type="file"
                        accept=".pdf"
                        style="display: none"
                        @change="onFileSelected"
                    />

                    <v-text-field
                        v-if="selectedFile"
                        v-model="fileName"
                        label="Nom du fichier (optionnel)"
                        hint="Laissez vide pour garder le nom original"
                        persistent-hint
                        prepend-icon="rename-box"
                        class="mt-4"
                    ></v-text-field>

                    <v-alert
                        v-if="uploadMessage"
                        :type="uploadSuccess ? 'success' : 'error'"
                        :value="true"
                        class="mt-3"
                    >
                        {{ uploadMessage }}
                    </v-alert>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        v-if="selectedFile"
                        color="grey"
                        text
                        @click="clearSelection"
                        :disabled="isUploading"
                    >
                        <v-icon left>close</v-icon>
                        Annuler
                    </v-btn>
                    <v-btn
                        color="primary"
                        :disabled="!selectedFile || isUploading"
                        :loading="isUploading"
                        @click="uploadDocument"
                    >
                        <v-icon left>upload</v-icon>
                        Mettre à jour
                    </v-btn>
                </v-card-actions>
            </v-card>

            <!-- Liste des documents actuels -->
            <v-card class="mt-4 pa-4">
                <v-card-title>
                    <h3>Documents actuels</h3>
                    <v-spacer></v-spacer>
                    <v-btn
                        small
                        color="info"
                        outlined
                        @click="loadCurrentDocuments"
                        :loading="loadingDocs"
                    >
                        <v-icon left small>refresh</v-icon>
                        Actualiser
                    </v-btn>
                </v-card-title>

                <v-card-text>
                    <v-list v-if="currentDocuments.length > 0" two-line>
                        <v-list-tile
                            v-for="doc in currentDocuments"
                            :key="doc.name"
                            :style="doc === selectedDoc ? 'background-color:#00bcd4;border:1px solid black;border-radius:8px' : ''"
                            @click="setCurrentDocument(doc)"
                        >
                            <v-list-tile-avatar>
                                <v-icon v-if="doc === selectedDoc" color="white">file_open</v-icon>
                                <v-icon v-else color="primary">insert_drive_file</v-icon>
                            </v-list-tile-avatar>

                            <v-list-tile-content>
                                <v-list-tile-title>
                                    <span :class="{ 'font-weight-bold': doc === selectedDoc }">
                                        {{ doc.name }}
                                    </span>
                                </v-list-tile-title>
                                <v-list-tile-sub-title>{{ doc.size }} - {{ doc.modified }}</v-list-tile-sub-title>
                            </v-list-tile-content>

                            <v-list-tile-action>
                                <div class="d-flex align-center">
                                    <v-btn
                                        small
                                        color="primary"
                                        outlined
                                        class="mr-2"
                                        @click="previewDocument(doc)"
                                    >
                                        <v-icon small left>visibility</v-icon>
                                        Voir
                                    </v-btn>
                                    <v-btn
                                        small
                                        color="error"
                                        outlined
                                        @click="confirmDeleteDocument(doc)"
                                    >
                                        <v-icon small>delete</v-icon>
                                    </v-btn>
                                </div>
                            </v-list-tile-action>
                        </v-list-tile>
                    </v-list>

                    <v-alert
                        v-else
                        type="info"
                        outlined
                        :value="true"
                    >
                        Aucun document trouvé dans le dossier
                    </v-alert>
                </v-card-text>
            </v-card>

            <!-- Dialog de confirmation de suppression -->
            <v-dialog v-model="deleteDialog" max-width="500">
                <v-card>
                    <v-card-title class="headline">
                        Confirmer la suppression
                    </v-card-title>
                    <v-card-text>
                        Êtes-vous sûr de vouloir supprimer le document <strong>{{ documentToDelete?.name }}</strong> ?
                        Cette action est irréversible.
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text @click="deleteDialog = false">
                            Annuler
                        </v-btn>
                        <v-btn
                            color="error"
                            text
                            @click="deleteDocument"
                            :loading="deletingDocument"
                        >
                            Supprimer
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </div>
    </v-container>
</template>

<script>
import $axios from '../../api';

export default {
    name: 'UpdateDocGnc',
    data() {
        return {
            selectedFile: null,
            selectedDoc: null,
            fileName: '',
            isUploading: false,
            loadingDocs: false,
            uploadMessage: '',
            uploadSuccess: false,
            currentDocuments: [],
            isDragOver: false,
            dragCounter: 0,
            selectedDocument: null,
            deleteDialog: false,
            documentToDelete: null,
            deletingDocument: false,
        }
    },
    mounted() {
        this.loadCurrentDocuments();
        this.preventDefaultDragBehavior();
    },
    beforeDestroy() {
        this.removeDefaultDragBehavior();
    },
    methods: {
        onFileSelected(event) {
            const file = event.target.files[0];
            this.processFile(file);
        },

        onDragEnter(event) {
            event.preventDefault();
            this.dragCounter++;
            this.isDragOver = true;
        },

        onDragOver(event) {
            event.preventDefault();
        },

        onDragLeave(event) {
            event.preventDefault();
            this.dragCounter--;
            if (this.dragCounter === 0) {
                this.isDragOver = false;
            }
        },

        onDrop(event) {
            event.preventDefault();
            this.isDragOver = false;
            this.dragCounter = 0;

            const files = event.dataTransfer.files;
            if (files.length > 0) {
                this.processFile(files[0]);
            }
        },

        clearSelection() {
            this.selectedFile = null;
            this.fileName = '';
            this.uploadMessage = '';
            this.$refs.fileInput.value = '';
        },

        preventDefaultDragBehavior() {
            // Empêcher le comportement par défaut sur toute la page
            const events = ['dragenter', 'dragover', 'dragleave', 'drop'];
            events.forEach(event => {
                document.addEventListener(event, this.preventDefaults, false);
            });
        },

        removeDefaultDragBehavior() {
            const events = ['dragenter', 'dragover', 'dragleave', 'drop'];
            events.forEach(event => {
                document.removeEventListener(event, this.preventDefaults, false);
            });
        },

        preventDefaults(event) {
            event.preventDefault();
            event.stopPropagation();
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 B';
            const k = 1024;
            const sizes = ['B', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        previewDocument(doc) {
            console.log('👁️ Prévisualisation du document:', doc);
            this.selectedDocument = {
                ...doc,
                previewUrl: doc.previewUrl + '?v=' + Date.now() // ✅ Cache busting
            };
            this.openInNewTab();
        },

        openInNewTab() {
            if (this.selectedDocument) {
                window.open(this.selectedDocument.previewUrl, '_blank');
            }
        },

        confirmDeleteDocument(doc) {
            this.documentToDelete = doc;
            this.deleteDialog = true;
        },

        setCurrentDocument(doc) {
            this.selectedDoc = doc;
            $axios.post(`/gnc/current-document/${doc.name}`);
        },

        async deleteDocument() {
            if (!this.documentToDelete) return;

            this.deletingDocument = true;
            try {
                const response = await $axios.get(`/gnc/document/${this.documentToDelete.name}`);

                if (response.data.success) {
                    console.log("FLAG => if");
                    this.uploadMessage = response.data.message;
                    this.uploadSuccess = true;

                    // Recharger la liste des documents
                    await this.loadCurrentDocuments();

                    setTimeout(() => {
                        this.uploadMessage = '';
                    }, 3000);
                } else {
                    throw new Error(response.data.message);
                }

            } catch (error) {
                console.error('Erreur lors de la suppression:', error);
                this.uploadMessage = error.response?.data?.message || 'Erreur lors de la suppression';
                this.uploadSuccess = false;
            } finally {
                this.deletingDocument = false;
                this.deleteDialog = false;
                this.documentToDelete = null;
            }
        },

        async uploadDocument() {
            if (!this.selectedFile) return;

            console.log('🚀 Starting upload:', {
                fileName: this.selectedFile.name,
                fileSize: this.selectedFile.size,
                customName: this.fileName,
            });

            this.isUploading = true;
            this.uploadMessage = '';

            try {
                const formData = new FormData();
                formData.append('document', this.selectedFile);

                if (this.fileName.trim()) {
                    formData.append('fileName', this.fileName.trim());
                }

                console.log('📤 FormData created, sending request...');

                const response = await $axios.post('/gnc/upload-doc', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    timeout: 60000,
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        console.log(`📊 Upload progress: ${percentCompleted}%`);
                    }
                });

                console.log('✅ Upload successful:', response.data);

                this.uploadMessage = response.data.message || 'Document mis à jour avec succès !';
                this.uploadSuccess = true;
                this.selectedFile = null;
                this.fileName = '';
                if (this.$refs.fileInput) this.$refs.fileInput.value = '';

                await this.loadCurrentDocuments();

                setTimeout(() => {
                    this.uploadMessage = '';
                }, 7000);

            } catch (error) {
                console.error('❌ Upload error:', error);

                if (error.response) {
                    console.error('Response status:', error.response.status);
                    console.error('Response data:', error.response.data);

                    if (error.response.status === 413 || error.response.status === 422) {
                        this.uploadMessage = `🚨 Erreur serveur: ${error.response.data.message || 'Fichier trop volumineux'}

                        📊 Informations debug:
                        - Taille fichier: ${this.formatFileSize(this.selectedFile.size)}
                        - Limite PHP: upload_max_filesize = 2M
                        - Limite Laravel: max:1800KB (pour sécurité)

                        💡 La compression n'a pas suffi. Contactez l'administrateur.`;
                    } else {
                        this.uploadMessage = error.response?.data?.message || 'Erreur lors de la mise à jour du document';
                    }
                } else {
                    this.uploadMessage = 'Erreur de connexion au serveur';
                }

                this.uploadSuccess = false;
            } finally {
                this.isUploading = false;
            }
        },

        async loadCurrentDocuments() {
            this.loadingDocs = true;
            try {
                const response = await $axios.get('/gnc/documents');
                this.currentDocuments = response.data.documents || [];
                this.currentDocuments.forEach(doc => {
                    if(doc.current){
                        this.selectedDoc = doc;
                    }
                });
                console.log('📋 Documents chargés:', this.currentDocuments);
            } catch (error) {
                console.error('Erreur lors du chargement des documents:', error);
                this.uploadMessage = 'Erreur lors du chargement des documents';
                this.uploadSuccess = false;
            } finally {
                this.loadingDocs = false;
            }
        },

        async processFile(file) {
            this.uploadMessage = '';
            this.fileName = '';

            if (!file) {
                this.selectedFile = null;
                return;
            }

            console.log('📁 Processing file:', {
                name: file.name,
                size: file.size,
                type: file.type,
                sizeInMB: (file.size / (1024 * 1024)).toFixed(2)
            });

            if (file.type !== 'application/pdf') {
                this.uploadMessage = 'Veuillez sélectionner un fichier PDF';
                this.uploadSuccess = false;
                this.selectedFile = null;
                if (this.$refs.fileInput) this.$refs.fileInput.value = '';
                return;
            }

            this.selectedFile = file;
        },
    }
}
</script>

<style scoped>
.drop-zone {
    border: 2px dashed #ccc;
    border-radius: 8px;
    padding: 40px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    background-color: #fafafa;
}

.drop-zone--active {
    border-color: #1976d2;
    background-color: #e3f2fd;
}

.drop-zone--has-file {
    border-color: #4caf50;
    background-color: #e8f5e8;
}

.drop-zone:hover {
    border-color: #1976d2;
    background-color: #f5f5f5;
}

.compression-info {
    border-left: 4px solid #2196f3;
    padding-left: 16px;
    background-color: #f5f5f5;
    border-radius: 4px;
}

.v-dialog--fullscreen .v-card {
    display: flex;
    flex-direction: column;
}

.v-dialog--fullscreen .v-card__text {
    flex: 1;
    overflow: hidden;
}
</style>
